import { useState } from 'react';
import { Calendar } from 'lucide-react';

const PRESETS = [
  { id: '7d', label: '7 dias', days: 7 },
  { id: '30d', label: '30 dias', days: 30 },
  { id: '90d', label: '90 dias', days: 90 },
];

// "to" é sempre agora — a janela sempre termina no presente para os presets.
function presetRange(days) {
  const to = new Date();
  const from = new Date(to.getTime() - days * 86400000);
  return { from, to };
}

function toDateInput(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/** Período padrão (30 dias, igual ao default do backend — DEFAULT_WINDOW_DAYS). */
export function defaultPeriod(days = 30) {
  return { preset: `${days}d`, ...presetRange(days) };
}

/**
 * Seletor de período das telas de métricas (M6b): presets fixos + intervalo
 * personalizado. Controlado por fora — `value` é `{ preset, from, to }`
 * (from/to sempre Date), `onChange` recebe o mesmo formato. Quem usa decide
 * quando refazer a busca (efeito no componente pai, não aqui).
 */
export default function PeriodPicker({ value, onChange }) {
  const [customOpen, setCustomOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(() => toDateInput(value?.from));
  const [draftTo, setDraftTo] = useState(() => toDateInput(value?.to));

  const applyPreset = (preset) => {
    setCustomOpen(false);
    onChange({ preset: preset.id, ...presetRange(preset.days) });
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) return;
    const from = new Date(`${draftFrom}T00:00:00.000Z`);
    const to = new Date(`${draftTo}T23:59:59.999Z`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from >= to) return;
    onChange({ preset: 'custom', from, to });
    setCustomOpen(false);
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {PRESETS.map((p) => (
        <button
          key={p.id}
          onClick={() => applyPreset(p)}
          className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value?.preset === p.id ? 'bg-accent text-white' : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
          }`}
        >
          {p.label}
        </button>
      ))}
      <div className="relative">
        <button
          onClick={() => setCustomOpen((v) => !v)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value?.preset === 'custom' ? 'bg-accent text-white' : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
          }`}
        >
          <Calendar className="w-3.5 h-3.5" />
          {value?.preset === 'custom' ? `${draftFrom} – ${draftTo}` : 'Personalizado'}
        </button>
        {customOpen && (
          <div
            className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-xl p-3 z-30 w-64"
            onClick={(e) => e.stopPropagation()}
          >
            <label className="block text-[11px] text-text-secondary mb-1">De</label>
            <input
              type="date"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              className="w-full bg-main border border-border rounded px-2 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50 mb-2"
            />
            <label className="block text-[11px] text-text-secondary mb-1">Até</label>
            <input
              type="date"
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              className="w-full bg-main border border-border rounded px-2 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50 mb-3"
            />
            <button
              onClick={applyCustom}
              disabled={!draftFrom || !draftTo}
              className="w-full px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-md hover:bg-accent/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Aplicar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
